import { InputContainer } from "@/components/ui/forms/inputContainer"
import { Switch } from "@/components/ui/baseUi/switch"
import { Text } from "@/components/ui/baseUi/text"
import { type FieldValueChange } from "@/typings/formik"
import { word } from "@modules/core/utils/i18n"
import { ProgramVisibility } from "@modules/programs/types/programTypes"
import { type FieldInputProps } from "formik"
import { useEffect, useState } from "react"

type ProgramBundleVisibilityInputProps<T, K> = {
  label: string
  name: string
  isRequiredInput: boolean
  setFieldValue: FieldValueChange<K>
  getFieldProps: (nameOrOptions: T) => FieldInputProps<any>
  error?: React.JSX.Element
  defaultType?: ProgramVisibility
  isSwitchEnabled?: boolean
  switchProps?: React.ComponentProps<typeof Switch>
  className?: string
} & React.ComponentProps<"div">

export function ProgramBundleVisibilityInput<T, K>({
  label,
  name,
  isRequiredInput,
  setFieldValue,
  getFieldProps,
  error,
  isSwitchEnabled = true,
  switchProps,
  className,
  ...props
}: ProgramBundleVisibilityInputProps<T, K>): React.JSX.Element {
  const programVisibility = getFieldProps(name as T).value as ProgramVisibility
  const [isPublic, setIsPublic] = useState(programVisibility === ProgramVisibility.PUBLIC)

  useEffect(() => {
    setIsPublic(programVisibility === ProgramVisibility.PUBLIC)
  }, [programVisibility])

  const handleVisibilityChange = (visibility: ProgramVisibility) => {
    if (isSwitchEnabled) {
      setFieldValue(name, visibility)
      setIsPublic(visibility === ProgramVisibility.PUBLIC)
    }
  }

  return (
    <InputContainer
      isRequiredInput={isRequiredInput}
      label={label}
      name={name}
      error={error}
      className={className}
      {...props}
    >
      <div className="card flex w-full flex-col gap-2">
        {/* CONTENT */}
        <div className="flex w-full flex-row gap-2">
          <div className="flex flex-col gap-2">
            <Text fontStyle="LM" className="text-textDefault-heading">
              {word("programBundle.visibilityInput.title")}
            </Text>
            <Text fontStyle="BS" className="text-textDefault-heading">
              {word("programBundle.visibilityInput.subtitle")}
            </Text>
          </div>

          <Switch
            checked={isPublic}
            onCheckedChange={() => {
              handleVisibilityChange(isPublic ? ProgramVisibility.PRIVATE : ProgramVisibility.PUBLIC)
            }}
            {...switchProps}
          />
        </div>
        {/* ALERTE */}
        <div className="relative mt-4 flex">
          <div className="absolute right-0 left-0 h-full w-1 bg-custom-orange-500" />
          <Text fontStyle="LS" className="pl-2 text-textDefault-heading italic">
            {word("programBundle.visibilityInput.alert")}
          </Text>
        </div>
      </div>
    </InputContainer>
  )
}
